// Stage 2, planning (spec §2). A full-bleed map with one rail: the plan. You draw the course of action
// on the ground with gesture tools, every graphic becomes a card, and you task units by dragging their
// cards onto a graphic or onto the map and picking what they are to do. A real-time clock runs the
// whole time; at zero the fight starts with whatever plan exists.
//
// Nothing here is a dropdown. Tools are cards shaped like the graphic they draw, tasks are chips, and
// the target of a task is a gesture on the map (spec §8).
//
// ---- pitch 4, "planning stage that shows the plan" -------------------------------------------------
// The independent review (docs/reviews/2026-09-21-independent-review.md A.2) found the stage showing
// everything except the plan: the fitted objective sat at y = 14,975 m of 15,000, so the camera put it
// under the top bar; the assembly area clipped off the bottom; the advisors were a thousand pixels down
// a single scrolling rail; and the picker that asks "what is this platoon to do?" opened seven hundred
// pixels from where the platoon had just been dropped. Four answers, in the order they are built below:
//
//   1. The camera is fitted with asymmetric padding that reserves the rail, the top bar and the task
//      organization strip, so the whole AO lands in the box the player can actually see.
//   2. The rail is two-tier: a compact header (tools, then the control graphics as a count chip that
//      expands), then the plan, then the advisors — above the fold — then the wind tunnel. The task
//      organization left the rail entirely for a strip along the bottom of the map.
//   3. The task picker is a popover at the drop point. Drop and decision are in the same place.
//   4. A grid readout under the cursor and a toggleable reporting grid, so "grid 5071" is somewhere a
//      finger can be put on the map rather than a number with no referent.
// ---- end pitch 4 -----------------------------------------------------------------------------------

import {
  useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties,
} from 'react';
import { MapView, type MapReady } from '../../map/MapView';
import { Overlay, type OverlayUnit } from '../../render/Overlay';
import { useStore } from '../../state/store';
import { card } from '../../sim/deck';
import { UNIT_DEFS } from '../../sim/scenario';
import type { Graphic } from '../../sim/types';
import { aoOrientation, startTool, TOOLS, type DrawKind, type DrawSession, type NameContext } from '../../map/draw';
import { Chip } from '../cards/ui';
import { GraphicCard, ShapeGlyph } from '../cards/GraphicCard';
import { AcronymText } from '../Acronym';
import { Wordmark } from '../Wordmark';
import { PlanClock } from '../PlanClock';
import { symbolUrl } from '../symbol';
import {
  aimPoint, cardForTask, defaultTask, describeTarget, gridOverlayCellM, railGroups, spreadForDisplay,
  suggestedOpening, targetForDrop, TASKS, TOOL_SHORT, type Drop, type TaskId,
} from '../plan';
import { Advisors } from '../advisors/Advisors';
// ---- wind tunnel (pitch 1) ------------------------------------------------------------------------
// The whole panel lives in src/ui/planning/WindTunnel.tsx; this import and the `<WindTunnel />` below
// the plan are the only two lines it costs this file.
import { WindTunnel } from '../planning/WindTunnel';
// ---- end wind tunnel ------------------------------------------------------------------------------
import { useAdvisors } from '../../state/advisors';
import { gridRef } from '../reports';
import { DEBUG } from '../../debug';

const RAIL = 320;
/** The top bar's height, and the top of the rail. */
const HEADER_H = 48;
/** The task organization strip along the bottom of the map. */
const TASKORG_H = 72;
/**
 * Camera fit padding, in screen pixels (pitch 4, item 1). The map is full-bleed under everything, so
 * the only part of it the player can actually read is the box left over once the top bar, the plan
 * rail and the task organization strip have taken their share — and the fitter puts the objective
 * within 25 m of the AO's north edge and the assembly area within 25 m of its south edge, so a
 * symmetric 24 px put both of them under furniture. Verified at 1440x900 and 1280x800 by projecting
 * the objective's centre and the assembly area's centroid and checking both land inside this box.
 */
const FIT_PADDING = { top: HEADER_H + 48, bottom: TASKORG_H + 48, left: RAIL + 24, right: 24 };
/** How close, in screen pixels, a drop has to land to count as landing on a control graphic. */
const HIT_PX = 24;
/** Minimum separation between two unit symbols in the assembly area, in AO centimeters (600 m, about
 * 30 screen pixels at the fitted camera - one symbol width of clear air). Display only: it fans the
 * symbols apart so they can be told apart and clicked (see `spreadForDisplay`). */
const ASSEMBLY_SPREAD_CM = 60_000;
/** Backstop for letting the advisors start calling the model when the map never goes idle. */
const ADVISOR_RELEASE_MS = 4000;
/** How long a just-tasked unit's symbol wears its ring, in milliseconds. */
const COMMIT_FLASH_MS = 600;

/** The key the drag carries: the unit's id, which is its index in the scenario's blue placements. */
const DRAG_TYPE = 'text/plain';

function distToSegment(px: number, py: number, a: { x: number; y: number }, b: { x: number; y: number }): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return Math.hypot(px - a.x, py - a.y);
  const t = Math.max(0, Math.min(1, ((px - a.x) * dx + (py - a.y) * dy) / len2));
  return Math.hypot(px - (a.x + t * dx), py - (a.y + t * dy));
}

/**
 * A unit dropped, waiting on a task.
 *
 * `at` is where the drop landed in the stage's own pixel space — the popover is anchored there, which
 * is the whole point of pitch 4's third item. A drop onto a graphic card in the rail has no screen
 * point of its own, so the stage supplies the graphic's own position on the map instead.
 */
interface Pending { unitId: number; drop: Drop; at: { x: number; y: number } }

export function PlanningStage() {
  const meta = useStore((s) => s.meta);
  const scenario = useStore((s) => s.scenario);
  const grid = useStore((s) => s.grid);
  const planDeadline = useStore((s) => s.planDeadline);
  const executePlan = useStore((s) => s.executePlan);

  const overlayRef = useRef<Overlay | null>(null);
  const sessionRef = useRef<DrawSession | null>(null);
  /** Set once the map is up, so effects that drive the map imperatively can wait for it. */
  const [mapEpoch, setMapEpoch] = useState(0);
  /** Projects AO centimeters to stage pixels; null until the map is ready. */
  const toScreenRef = useRef<((p: { x: number; y: number }) => { x: number; y: number }) | null>(null);
  /** Redraws the assembly area symbols; owned by the map effect, called when the flash changes. */
  const redrawUnitsRef = useRef<(() => void) | null>(null);
  /** The unit whose symbol is wearing the just-tasked ring, read by the overlay's draw pass. */
  const flashRef = useRef<number | null>(null);
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** The hover grid readout is written straight into the DOM: a pointermove is not a state change. */
  const readoutRef = useRef<HTMLSpanElement>(null);

  const [tool, setTool] = useState<DrawKind | null>(null);
  const [points, setPoints] = useState(0);
  const [pending, setPending] = useState<Pending | null>(null);
  const [taskError, setTaskError] = useState<string | null>(null);
  const [dropHint, setDropHint] = useState<string | null>(null);
  const [gridOn, setGridOn] = useState(false);

  // ---- drawing sessions --------------------------------------------------------------------------

  /** Everything the namer needs, read fresh so a graphic deleted mid-session frees its name again. */
  const nameContext = useCallback((): NameContext => {
    const s = useStore.getState();
    const m = s.meta;
    return {
      existing: s.scenario?.graphics ?? [],
      orientation: s.scenario ? aoOrientation(s.scenario) : 'WE',
      widthCm: (m?.w ?? 300) * (m?.cellM ?? 50) * 100,
      heightCm: (m?.h ?? 300) * (m?.cellM ?? 50) * 100,
    };
  }, []);

  const endSession = useCallback(() => {
    sessionRef.current = null;
    setTool(null);
    setPoints(0);
  }, []);

  const beginTool = useCallback((kind: DrawKind) => {
    sessionRef.current?.cancel();
    const session = startTool(kind, nameContext, {
      onPreview: (g) => overlayRef.current?.setPreview(g),
      onDone: (g) => { useStore.getState().addGraphic(g); endSession(); },
      onCancel: endSession,
    });
    sessionRef.current = session;
    setTool(kind);
    setPoints(0);
  }, [endSession, nameContext]);

  const toggleTool = useCallback((kind: DrawKind) => {
    if (sessionRef.current?.kind === kind) { sessionRef.current.cancel(); return; }
    beginTool(kind);
  }, [beginTool]);

  // Enter finishes the graphic in progress, Escape throws it away — wherever the focus is, as long as
  // it is not inside a rename field (those handle their own keys and stop the event). Escape also
  // closes the task popover, which is what a popover anchored to the map has to answer to.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      const session = sessionRef.current;
      if (!session) {
        if (e.key === 'Escape') { setPending(null); setTaskError(null); }
        return;
      }
      if (e.key === 'Enter') { e.preventDefault(); if (!session.finish()) session.cancel(); }
      else if (e.key === 'Escape') { e.preventDefault(); session.cancel(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => () => { if (flashTimer.current) clearTimeout(flashTimer.current); }, []);

  // ---- the map -----------------------------------------------------------------------------------

  const handleReady = useCallback(({ map, container, projection }: MapReady) => {
    const overlay = new Overlay(container, map, projection);
    overlayRef.current = overlay;
    // The advisors hold a connection per model call; they wait until the map has its tiles, so
    // MapLibre gets the browser's six same-origin connections first (see src/state/advisors.ts
    // `mapReady`). The timer is the backstop: on a dead network `idle` may never come, and the rail
    // should still ask for plays rather than sit on templates forever.
    const releaseAdvisors = (): void => useAdvisors.getState().setMapReady(true);
    if (map.loaded()) releaseAdvisors();
    else map.once('idle', releaseAdvisors);
    const advisorRelease = setTimeout(releaseAdvisors, ADVISOR_RELEASE_MS);
    // Drawing is the whole point of this stage; a double-click finishes a graphic and must not zoom.
    map.doubleClickZoom.disable();

    const toScreen = (p: { x: number; y: number }): { x: number; y: number } => {
      const [lng, lat] = projection.cmToLngLat(p.x, p.y);
      const s = map.project([lng, lat]);
      return { x: s.x, y: s.y };
    };
    toScreenRef.current = toScreen;

    const distToGraphic = (g: Graphic, px: number, py: number): number => {
      const pts = g.pts.map(toScreen);
      if (pts.length === 0) return Infinity;
      if (pts.length === 1) return Math.hypot(pts[0].x - px, pts[0].y - py);
      let best = Infinity;
      const closed = g.kind === 'obj' || g.kind === 'aa';
      for (let i = 0; i < pts.length; i++) {
        const next = pts[i + 1] ?? (closed ? pts[0] : null);
        best = Math.min(best, Math.hypot(pts[i].x - px, pts[i].y - py));
        if (next) best = Math.min(best, distToSegment(px, py, pts[i], next));
      }
      return best;
    };

    /** The blue force in its assembly area: the task organization, on the ground, before H-hour. */
    const drawUnits = (s = useStore.getState()): void => {
      const sc = s.scenario;
      if (!sc) return;
      const shown = spreadForDisplay(sc.blue, ASSEMBLY_SPREAD_CM);
      const units: OverlayUnit[] = sc.blue.map((p, id) => ({
        id, x: shown[id].x, y: shown[id].y,
        sidc: UNIT_DEFS[p.type].sidcBlue,
        side: 0 as const,
        strength: 100,
        suppression: 0,
        selected: id === s.selectedUnit,
        // The just-tasked ring (pitch 4): the same dashed ring an order in flight wears in the live
        // stage, for `COMMIT_FLASH_MS` — the popover closes, and the answer to "did that land on the
        // right platoon?" is on the map rather than only in the rail.
        pending: id === flashRef.current,
        label: p.callsign,
      }));
      overlay.setUnits(units);
      overlay.setGraphics(sc.graphics);
    };
    drawUnits();
    redrawUnitsRef.current = () => drawUnits();

    const unsubscribe = useStore.subscribe((s, prev) => {
      if (s.scenario !== prev.scenario || s.selectedUnit !== prev.selectedUnit) drawUnits(s);
    });

    const cmAt = (clientX: number, clientY: number): { x: number; y: number } => {
      const rect = container.getBoundingClientRect();
      const ll = map.unproject([clientX - rect.left, clientY - rect.top]);
      return projection.lngLatToCm(ll.lng, ll.lat);
    };

    overlay.onClickMap((cm) => {
      const session = sessionRef.current;
      if (!session) { useStore.getState().select(null); return; }
      session.addPoint(cm);
      setPoints(session.points().length);
    });

    overlay.onClickUnit((id) => {
      const session = sessionRef.current;
      if (session) {
        // While a tool is up, a click on a unit symbol is still a point on the ground.
        const p = useStore.getState().scenario?.blue[id];
        if (p) { session.addPoint({ x: p.x, y: p.y }); setPoints(session.points().length); }
        return;
      }
      useStore.getState().select(id);
    });

    const onPointerMove = (e: PointerEvent): void => {
      const cm = cmAt(e.clientX, e.clientY);
      sessionRef.current?.hover(cm);
      // The grid under the cursor (pitch 4, item 4). Written straight into the span: a pointer moving
      // across the map at 60 Hz is not a reason to re-render a React tree.
      const node = readoutRef.current;
      const g = useStore.getState().grid;
      if (node && g) node.textContent = `grid ${gridRef(g.cellOfCm(cm.x, cm.y), g)}`;
    };
    const onDblClick = (e: MouseEvent): void => {
      const session = sessionRef.current;
      if (!session) return;
      e.preventDefault();
      if (!session.finish()) session.cancel();
    };
    const onDragOver = (e: DragEvent): void => {
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    };
    const onDrop = (e: DragEvent): void => {
      e.preventDefault();
      const raw = e.dataTransfer?.getData(DRAG_TYPE);
      if (!raw) return;
      const unitId = Number(raw);
      if (!Number.isInteger(unitId)) return;
      const rect = container.getBoundingClientRect();
      const px = e.clientX - rect.left;
      const py = e.clientY - rect.top;
      // A drop that lands on a control graphic targets that graphic; anywhere else is a point on the
      // ground. Same hit tolerance the live stage uses for targeting a graphic.
      let best: { g: Graphic; d: number } | null = null;
      for (const g of useStore.getState().scenario?.graphics ?? []) {
        const d = distToGraphic(g, px, py);
        if (d <= HIT_PX && (!best || d < best.d)) best = { g, d };
      }
      setTaskError(null);
      setPending({
        unitId,
        drop: best ? { kind: 'graphic', graphic: best.g } : { kind: 'point', at: cmAt(e.clientX, e.clientY) },
        at: { x: px, y: py },
      });
      setDropHint(null);
    };

    container.addEventListener('pointermove', onPointerMove);
    container.addEventListener('dblclick', onDblClick);
    container.addEventListener('dragover', onDragOver);
    container.addEventListener('drop', onDrop);

    // Handles for the end-to-end test and for poking at the plan from the console.
    // Development and `?debug` only (src/debug.ts), same as the live stage's `__wt`.
    if (DEBUG) (window as unknown as Record<string, unknown>).__wtPlan = { overlay, store: useStore, map, projection };
    setMapEpoch((n) => n + 1);

    return () => {
      clearTimeout(advisorRelease);
      useAdvisors.getState().setMapReady(false);
      unsubscribe();
      container.removeEventListener('pointermove', onPointerMove);
      container.removeEventListener('dblclick', onDblClick);
      container.removeEventListener('dragover', onDragOver);
      container.removeEventListener('drop', onDrop);
      overlay.destroy();
      overlayRef.current = null;
      toScreenRef.current = null;
      redrawUnitsRef.current = null;
      if (DEBUG) delete (window as unknown as Record<string, unknown>).__wtPlan;
    };
  }, []);

  // The map cursor says a tool is live.
  useEffect(() => {
    const host = document.querySelector('[data-testid="planning-stage"] .maplibregl-map')?.parentElement;
    host?.classList.toggle('targeting-cursor', tool !== null);
  }, [tool]);

  // The reporting grid overlay, on or off. `mapEpoch` is in the dependencies so a toggle made before
  // the map finished loading is applied the moment it does.
  const overlayCellM = useMemo(() => (grid ? gridOverlayCellM(grid) : null), [grid]);
  useEffect(() => {
    overlayRef.current?.setGrid(gridOn && overlayCellM ? overlayCellM : null);
  }, [gridOn, overlayCellM, mapEpoch]);

  if (!meta || !scenario || !grid) {
    return (
      <div className="flex h-full items-center justify-center">
        <span className="mono text-xs tracking-[0.3em] text-fog">NO AREA OF OPERATIONS LOADED</span>
      </div>
    );
  }

  const activeTool = TOOLS.find((t) => t.kind === tool) ?? null;

  return (
    <div
      data-testid="planning-stage"
      className="relative h-full w-full overflow-hidden bg-ink"
      // Lift the map's required attribution clear of the task organization strip along the bottom
      // (read by `.maplibregl-ctrl-bottom-right` in styles.css).
      style={{ '--wt-attrib-bottom': `${TASKORG_H + 6}px`, '--wt-attrib-right': '6px' } as CSSProperties}
    >
      <MapView
        meta={meta}
        onReady={handleReady}
        fitPadding={FIT_PADDING}
      />

      <header className="absolute inset-x-0 top-0 z-30 flex h-12 items-center gap-3 border-b border-slate-700 bg-slate-900/90 px-3 backdrop-blur-sm">
        <Wordmark suffix="planning" />
        <PlanClock deadline={planDeadline} onExpire={onPlanClockExpired} />
        <span className="mono truncate text-[11px] text-paper">
          <AcronymText>{scenario.names.tf}</AcronymText>
        </span>
        <span className="mono truncate text-[11px] text-fog">{meta.title}</span>
        <div className="ml-auto flex items-center gap-2">
          <span className="mono text-[10px] text-fog">
            the enemy is already moving — at zero the fight starts with the plan as it stands
          </span>
          <ExecuteButton />
        </div>
      </header>

      {activeTool && (
        <div
          data-testid="draw-banner"
          className="fade-in pointer-events-none absolute left-1/2 top-16 z-20 -translate-x-1/2 rounded-sm border bg-slate-900/95 px-3 py-1.5"
          style={{ borderColor: activeTool.color }}
        >
          <div className="flex items-center gap-3">
            <span className="mono text-[11px] tracking-[0.05em]" style={{ color: activeTool.color }}>
              {activeTool.label.toLowerCase()}: {activeTool.hint} ({points} point{points === 1 ? '' : 's'})
            </span>
            <span className="pointer-events-auto flex gap-1">
              <Chip onClick={() => sessionRef.current?.finish()} testId="draw-finish" title="Enter, or double-click the last point">finish</Chip>
              <Chip onClick={() => sessionRef.current?.cancel()} testId="draw-cancel" title="Escape also cancels">cancel</Chip>
            </span>
          </div>
        </div>
      )}

      {dropHint && (
        <div className="pointer-events-none absolute left-1/2 top-16 z-20 -translate-x-1/2 rounded-sm border border-slate-700 bg-slate-900/95 px-3 py-1.5">
          <span className="mono text-[11px] text-fog">{dropHint}</span>
        </div>
      )}

      <aside
        data-testid="plan-rail"
        className="thin-scroll absolute left-0 top-12 z-20 flex flex-col gap-2 overflow-y-auto border-r border-slate-700 bg-slate-900/90 p-2 backdrop-blur-sm"
        style={{ width: RAIL, bottom: 0 }}
      >
        {/* Tier one: the two things you do to the map, compact enough that the plan and the advisors
            start inside the first third of the rail. */}
        <ToolStrip active={tool} onPick={toggleTool} />
        <GraphicsPanel onDropUnit={onDropUnitOnGraphicCard} />
        {/* Tier two: the plan, the staff that argues about it, and the wind tunnel that flies it. */}
        <PlanPanel />
        <Advisors context="planning" />
        {/* ---- wind tunnel (pitch 1): test the plan before committing to it ---- */}
        <WindTunnel />
        {/* ---- end wind tunnel ---- */}
      </aside>

      {/* The grid readout and the grid toggle, in the map's bottom-left corner: the four-digit
          reference the staff says on the net, under the cursor that is pointing at it. */}
      <div
        className="pointer-events-none absolute z-20 flex items-center gap-1.5"
        style={{ left: RAIL + 10, bottom: TASKORG_H + 10 }}
      >
        <span
          ref={readoutRef}
          data-testid="grid-readout"
          className="mono rounded-sm border border-slate-700 bg-slate-900/85 px-2 py-1 text-[11px] tracking-[0.08em] text-paper"
          title="The four-digit grid reference under the cursor — the same reference the plan and the report feed use"
        >
          grid ----
        </span>
        <span className="pointer-events-auto">
          <Chip
            onClick={() => setGridOn((on) => !on)}
            active={gridOn}
            testId="grid-toggle"
            title={
              overlayCellM
                ? `Draw the reporting grid: ${Math.round(overlayCellM)} m squares, each line a grid-reference boundary, labelled with its two digits`
                : 'Draw the reporting grid'
            }
          >
            grid
          </Chip>
        </span>
      </div>

      {/* The task organization, along the bottom of the map: drag a unit card up onto the ground and
          the picker opens where it landed. Drop and decision in one place (pitch 4, item 2). */}
      <TaskOrgBar
        onDragStartUnit={() => setDropHint('drop on a graphic, or on a point on the ground')}
        onDragEndUnit={() => setDropHint(null)}
      />

      {pending && (
        <TaskPopover
          pending={pending}
          error={taskError}
          onPick={(task) => pickTask(task)}
          onClose={() => { setPending(null); setTaskError(null); }}
        />
      )}
    </div>
  );

  /**
   * A drop onto a control graphic's card in the rail. The card has no position on the ground, so the
   * popover is anchored to the graphic itself — still the place the player is looking.
   */
  function onDropUnitOnGraphicCard(unitId: number, graphic: Graphic): void {
    const toScreen = toScreenRef.current;
    const p = toScreen ? toScreen(aimPoint(graphic)) : null;
    setTaskError(null);
    setDropHint(null);
    setPending({
      unitId,
      drop: { kind: 'graphic', graphic },
      at: p ?? { x: RAIL + 200, y: 240 },
    });
  }

  /**
   * Zero on the planning clock. The fight starts with the plan as it stands — but a task popover still
   * open is a decision the player was in the middle of making, and throwing it away without a word is
   * the one thing the clock must not do (review A.9). It is committed with the sensible default for
   * that unit, and the report feed opens with a line saying so.
   */
  function onPlanClockExpired(): void {
    const s = useStore.getState();
    if (pending && scenario) {
      const placement = scenario.blue[pending.unitId];
      if (placement) pickTask(defaultTask(placement.type));
      setPending(null);
      setTaskError(null);
    }
    const n = useStore.getState().plan.length;
    s.addNotice(`Planning time expired — plan executed as it stood (${n} order${n === 1 ? '' : 's'})`);
    void executePlan();
  }

  /** Marks a unit's symbol as just tasked for `COMMIT_FLASH_MS`, then clears it. */
  function flashUnit(unitId: number): void {
    flashRef.current = unitId;
    redrawUnitsRef.current?.();
    if (flashTimer.current) clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => {
      flashRef.current = null;
      redrawUnitsRef.current?.();
    }, COMMIT_FLASH_MS);
  }

  /** A task chip turns the drop into one order card, checked before it joins the plan. */
  function pickTask(task: TaskId): void {
    if (!pending || !scenario || !grid) return;
    const placement = scenario.blue[pending.unitId];
    if (!placement) return;
    const s = useStore.getState();

    if (task === 'reserve') {
      s.toggleReserve(pending.unitId);
      flashUnit(pending.unitId);
      setPending(null);
      setTaskError(null);
      return;
    }
    const cardId = cardForTask(task, placement.type);
    if (!cardId) return;
    const target = targetForDrop(cardId, pending.drop, grid);
    if (!target.ok) { setTaskError(target.reason); return; }
    const legal = s.addPlanOrder({ unitId: pending.unitId, card: cardId, target: target.target });
    if (!legal.ok) { setTaskError(legal.reason); return; }
    flashUnit(pending.unitId);
    setPending(null);
    setTaskError(null);
  }
}

// ---- top bar --------------------------------------------------------------------------------------

function ExecuteButton() {
  const executePlan = useStore((s) => s.executePlan);
  const planCount = useStore((s) => s.plan.length);
  const loading = useStore((s) => s.loading);
  return (
    <button
      type="button"
      data-testid="execute-plan"
      disabled={loading}
      onClick={() => void executePlan()}
      className="mono rounded-sm border px-3 py-1.5 text-[11px] uppercase tracking-[0.2em]"
      style={{
        borderColor: 'var(--color-friendly)',
        color: 'var(--color-friendly)',
        background: 'color-mix(in srgb, var(--color-friendly) 12%, transparent)',
      }}
      title="Start the fight now with the plan as it stands"
    >
      {loading ? 'starting…' : `Execute plan (${planCount})`}
    </button>
  );
}

// ---- rail panels ----------------------------------------------------------------------------------

function Panel({ title, note, children, testId }: { title: string; note?: React.ReactNode; children: React.ReactNode; testId?: string }) {
  return (
    <section data-testid={testId} className="wt-panel shrink-0">
      <header className="wt-panel-head">
        <span>{title}</span>
        {note && <span className="tracking-normal">{note}</span>}
      </header>
      <div className="p-2">{children}</div>
    </section>
  );
}

/**
 * The four drawing tools as one row of icon-and-label cards.
 *
 * They used to be a two-column grid of full-sentence cards a hundred pixels tall, which is a lot of
 * rail to spend on four verbs the player uses once each. The shape is still the graphic the tool
 * draws, and the full expansion and hint are still one hover away.
 */
function ToolStrip({ active, onPick }: { active: DrawKind | null; onPick: (k: DrawKind) => void }) {
  return (
    <section data-testid="draw-tools" className="wt-panel shrink-0">
      <header className="wt-panel-head">
        <span>Draw the course of action</span>
      </header>
      <div className="flex gap-1 p-1.5">
        {TOOLS.map((t) => {
          const on = active === t.kind;
          return (
            <button
              key={t.kind}
              type="button"
              data-testid={`tool-${t.kind}`}
              data-active={on ? 'true' : 'false'}
              onClick={() => onPick(t.kind)}
              title={`${t.label} — ${t.expansion}. ${t.hint}`}
              className="flex flex-1 flex-col items-center gap-0.5 rounded-sm border px-1 py-1"
              style={{
                borderColor: on ? t.color : 'var(--color-slate-700)',
                background: on ? `color-mix(in srgb, ${t.color} 14%, transparent)` : 'transparent',
              }}
            >
              <ShapeGlyph kind={t.kind} color={t.color} size={18} />
              <span className="mono text-[9px] uppercase tracking-[0.1em] text-paper">{TOOL_SHORT[t.kind]}</span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

/**
 * The control graphics, collapsed to a count chip.
 *
 * The fitted overlay alone is eleven cards on the NTC area of operations, and eleven cards of
 * furniture between the tools and the plan is how the advisors ended up a thousand pixels down the
 * rail. Closed it is one line; open it is the list, still droppable, still renamable.
 */
function GraphicsPanel({ onDropUnit }: { onDropUnit: (unitId: number, graphic: Graphic) => void }) {
  const scenario = useStore((s) => s.scenario)!;
  const renameGraphic = useStore((s) => s.renameGraphic);
  const removeGraphic = useStore((s) => s.removeGraphic);
  const [over, setOver] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const n = scenario.graphics.length;

  return (
    <section data-testid="graphics-panel" className="wt-panel shrink-0">
      <header className="wt-panel-head">
        <span>Control graphics</span>
        <span className="tracking-normal">
          <Chip
            onClick={() => setOpen((o) => !o)}
            active={open}
            testId="graphics-toggle"
            title={open ? 'Collapse the list' : 'List every control graphic; drop a unit card on one to task it'}
          >
            {n} graphic{n === 1 ? '' : 's'}
          </Chip>
        </span>
      </header>
      {open && (
        <div className="thin-scroll max-h-[220px] space-y-1 overflow-y-auto p-2">
          {scenario.graphics.map((g) => (
            <div
              key={g.id}
              onDrop={(e) => {
                e.preventDefault();
                setOver(null);
                const raw = e.dataTransfer.getData(DRAG_TYPE);
                const unitId = Number(raw);
                if (!raw || !Number.isInteger(unitId)) return;
                onDropUnit(unitId, g);
              }}
              onDragOver={(e) => { e.preventDefault(); setOver(g.id); }}
              onDragLeave={() => setOver((cur) => (cur === g.id ? null : cur))}
            >
              <GraphicCard
                graphic={g}
                drawn={g.id.startsWith('drawn-')}
                highlight={over === g.id}
                onRename={(name) => renameGraphic(g.id, name)}
                onRemove={() => removeGraphic(g.id)}
              />
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function PlanPanel() {
  const plan = useStore((s) => s.plan);
  const reserve = useStore((s) => s.reserve);
  const scenario = useStore((s) => s.scenario)!;
  const grid = useStore((s) => s.grid);
  const removePlanOrder = useStore((s) => s.removePlanOrder);
  const toggleReserve = useStore((s) => s.toggleReserve);
  const addPlanOrder = useStore((s) => s.addPlanOrder);
  const [suggestError, setSuggestError] = useState<string | null>(null);

  const fill = (): void => {
    if (!grid) return;
    const orders = suggestedOpening(scenario, grid);
    let refused: string | null = null;
    for (const o of orders) {
      const legal = addPlanOrder(o);
      if (!legal.ok) refused = legal.reason;
    }
    setSuggestError(refused);
  };

  return (
    <Panel title="Plan" note={`${plan.length} tasked`} testId="plan-panel">
      <div className="flex flex-wrap gap-1">
        <Chip
          onClick={fill}
          testId="suggested-opening"
          tone="friendly"
          title="Fill the plan with a sensible opening: two armor and two mech platoons attack the objective, a scout reconnoiters the first named area of interest, a drone launches over the second. Fires are left to you."
        >
          suggested opening
        </Chip>
      </div>
      {suggestError && <p className="mono mt-1 text-[10px] text-hostile">{suggestError}</p>}

      {/* Capped and scrolled: a sixteen-order plan must not push the advisors below the fold. */}
      <ul className="thin-scroll mt-1.5 max-h-[168px] space-y-1 overflow-y-auto pr-1">
        {plan.length === 0 && (
          <li className="mono text-[10px] leading-snug text-fog">
            No unit is tasked yet. Drag a unit card from the strip along the bottom onto a graphic or
            onto a point on the ground, then pick what it is to do.
          </li>
        )}
        {plan.map((o) => {
          const p = scenario.blue[o.unitId];
          return (
            <li
              key={o.unitId}
              data-testid={`plan-${o.unitId}`}
              className="flex items-center gap-2 rounded-sm border border-slate-700 bg-slate-800/60 px-2 py-1"
            >
              <span className="mono w-[34px] shrink-0 text-[11px] tracking-[0.05em] text-paper">{p?.callsign}</span>
              <span className="mono min-w-0 flex-1 truncate text-[10px] text-fog">
                <span style={{ color: 'var(--color-friendly)' }}>{card(o.card).label.toLowerCase()}</span>
                {' → '}
                {describeTarget(o.target, scenario.graphics, grid)}
              </span>
              <button
                type="button"
                onClick={() => removePlanOrder(o.unitId)}
                title="Remove this task"
                className="mono shrink-0 text-[10px] text-fog hover:text-[var(--color-hostile)]"
              >
                ✕
              </button>
            </li>
          );
        })}
        {reserve.map((id) => (
          <li key={`res-${id}`} className="flex items-center gap-2 rounded-sm border border-dashed border-slate-700 px-2 py-1">
            <span className="mono w-[34px] shrink-0 text-[11px] text-paper">{scenario.blue[id]?.callsign}</span>
            <span className="mono min-w-0 flex-1 text-[10px]" style={{ color: 'var(--color-command)' }}>RESERVE</span>
            <button
              type="button"
              onClick={() => toggleReserve(id)}
              title="Release from the reserve"
              className="mono shrink-0 text-[10px] text-fog hover:text-[var(--color-hostile)]"
            >
              ✕
            </button>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

// ---- the task organization strip --------------------------------------------------------------------

/**
 * The task organization as a strip along the bottom of the map (pitch 4, item 2).
 *
 * It was the last panel in a 320-pixel rail, which meant a platoon card was dragged from the far left
 * of the window to the ground it was being sent to and then the picker opened back where the card had
 * come from. Along the bottom of the map the drag is short, the drop is on the ground, and the picker
 * opens at the drop. Companies are chips; every card is draggable; the row scrolls sideways if the
 * task organization outgrows the window.
 */
function TaskOrgBar({ onDragStartUnit, onDragEndUnit }: { onDragStartUnit: () => void; onDragEndUnit: () => void }) {
  const scenario = useStore((s) => s.scenario)!;
  const plan = useStore((s) => s.plan);
  const reserve = useStore((s) => s.reserve);
  const selectedUnit = useStore((s) => s.selectedUnit);
  const select = useStore((s) => s.select);
  const groups = useMemo(() => railGroups(scenario), [scenario]);
  const tasked = useMemo(() => new Map(plan.map((o) => [o.unitId, o.card])), [plan]);

  return (
    <div
      data-testid="taskorg-bar"
      className="thin-scroll absolute bottom-0 z-20 flex items-center gap-2 overflow-x-auto border-t border-slate-700 bg-slate-900/92 px-2 backdrop-blur-sm"
      style={{ left: RAIL, right: 0, height: TASKORG_H }}
    >
      <span className="mono shrink-0 text-[9px] uppercase leading-tight tracking-[0.2em] text-fog">
        task<br />organization
      </span>
      {groups.map((g) => (
        <div key={g.key} data-testid={`taskorg-${g.key}`} className="flex shrink-0 items-center gap-1 border-l border-slate-700 pl-2">
          <span
            className="mono shrink-0 rounded-sm border px-1 py-0.5 text-[9px] uppercase tracking-[0.1em]"
            style={{ borderColor: 'var(--color-slate-700)', color: 'var(--color-friendly)' }}
            title={g.title}
          >
            {g.key === 'TF' ? 'TF' : `CO ${g.key}`}
          </span>
          {g.units.map((u) => {
            const has = tasked.get(u.id);
            const held = reserve.includes(u.id);
            return (
              <div
                key={u.id}
                draggable
                data-testid={`unit-${u.callsign}`}
                onDragStart={(e) => {
                  e.dataTransfer.setData(DRAG_TYPE, String(u.id));
                  e.dataTransfer.effectAllowed = 'copy';
                  select(u.id);
                  onDragStartUnit();
                }}
                onDragEnd={onDragEndUnit}
                onClick={() => select(u.id)}
                title={`${u.callsign} — ${u.label}. Drag it up onto the ground or onto a graphic to task it.`}
                className="flex w-[40px] shrink-0 cursor-grab flex-col items-center rounded-sm border px-0.5 py-0.5"
                style={{
                  borderColor: selectedUnit === u.id ? 'var(--color-friendly)' : 'var(--color-slate-700)',
                  background: has ? 'color-mix(in srgb, var(--color-friendly) 10%, transparent)' : 'transparent',
                }}
              >
                <img src={symbolUrl(u.sidc, 20)} alt="" width={20} height={20} draggable={false} />
                <span className="mono text-[9px] tracking-[0.02em] text-paper">{u.callsign}</span>
                {has && <span className="mono truncate text-[8px] uppercase" style={{ color: 'var(--color-friendly)' }}>{card(has).label}</span>}
                {held && <span className="mono text-[8px] uppercase" style={{ color: 'var(--color-command)' }}>res</span>}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ---- the task picker, at the drop ---------------------------------------------------------------------

/** Keeps the popover inside the readable map box: right of the rail, under the bar, above the strip. */
function clampToMap(
  at: { x: number; y: number }, w: number, h: number, hostW: number, hostH: number,
): { left: number; top: number } {
  const L = RAIL + 8;
  const R = hostW - 8;
  const T = HEADER_H + 8;
  const B = hostH - TASKORG_H - 8;
  let left = at.x + 14;
  if (left + w > R) left = at.x - 14 - w;
  let top = at.y + 14;
  if (top + h > B) top = at.y - 14 - h;
  return {
    left: Math.round(Math.max(L, Math.min(left, Math.max(L, R - w)))),
    top: Math.round(Math.max(T, Math.min(top, Math.max(T, B - h)))),
  };
}

/**
 * The task picker, as a popover at the drop point (pitch 4, item 3).
 *
 * The review's sharpest line about this stage was that direct manipulation which ends in a distant
 * rail is not direct manipulation. So: the chips open where the platoon landed, the target it is
 * about is named in the header, the reason a task was refused is inline under the chips, and Escape
 * or a click anywhere else puts it away.
 */
function TaskPopover({
  pending, error, onPick, onClose,
}: {
  pending: Pending; error: string | null; onPick: (t: TaskId) => void; onClose: () => void;
}) {
  const scenario = useStore((s) => s.scenario)!;
  const grid = useStore((s) => s.grid);
  const ref = useRef<HTMLDivElement>(null);
  const placement = scenario.blue[pending.unitId];
  const where = pending.drop.kind === 'graphic'
    ? pending.drop.graphic.name
    : grid
      ? `grid ${gridRef(grid.cellOfCm(pending.drop.at.x, pending.drop.at.y), grid)}`
      : 'that point';

  // First paint uses the measured window and the popover's nominal size; the layout effect corrects
  // it against what was actually laid out, before the browser paints either one.
  const [pos, setPos] = useState(() =>
    clampToMap(pending.at, 250, 150, window.innerWidth, window.innerHeight));

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const host = el.offsetParent as HTMLElement | null;
    const hostW = host?.clientWidth ?? window.innerWidth;
    const hostH = host?.clientHeight ?? window.innerHeight;
    setPos(clampToMap(pending.at, el.offsetWidth, el.offsetHeight, hostW, hostH));
  }, [pending, error]);

  // A press anywhere outside puts the popover away. Captured on the window so it beats the map's own
  // click handling; the popover's own presses are excluded by containment, not by stopping the event.
  useEffect(() => {
    const onDown = (e: PointerEvent): void => {
      if (ref.current?.contains(e.target as Node)) return;
      onClose();
    };
    window.addEventListener('pointerdown', onDown, true);
    return () => window.removeEventListener('pointerdown', onDown, true);
  }, [onClose]);

  return (
    <div
      ref={ref}
      data-testid="task-picker"
      className="fade-in absolute z-40 w-[250px] rounded-sm border bg-slate-900/97 p-2 shadow-lg"
      style={{ left: pos.left, top: pos.top, borderColor: 'var(--color-friendly)' }}
    >
      {/* A leader line back to the drop would be a second thing to keep in sync with the camera; the
          fourteen-pixel offset is short enough that the card is already at the cursor. */}
      <header className="flex items-center gap-2">
        <span className="mono text-[11px] tracking-[0.05em] text-paper">{placement?.callsign}</span>
        <span data-testid="task-target" className="mono min-w-0 flex-1 truncate text-[10px] text-fog">→ {where.toUpperCase()}</span>
        <Chip onClick={onClose} testId="task-cancel" title="Escape also closes this">cancel</Chip>
      </header>
      <div className="mt-1.5 flex flex-wrap gap-1">
        {TASKS.map((t) => (
          <Chip key={t.id} onClick={() => onPick(t.id)} testId={`task-${t.id}`} title={t.blurb}>
            {t.label}
          </Chip>
        ))}
      </div>
      {error && <p data-testid="task-error" className="mono mt-1.5 text-[10px] leading-snug text-hostile">{error}</p>}
    </div>
  );
}
